import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Globe2, Mail, Star, Users } from 'lucide-react';
import { axiosInstance } from '../../App';
import { CAPABILITIES } from '../../lib/access';
import { Card } from '../ui/card';

const Stat = ({ icon: Icon, label, value, hint }) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Icon size={14} aria-hidden="true" />{label}</div>
    <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </Card>
);

const scopeLabel = (leader) => (leader.scope === 'organisation' ? 'Whole organisation' : `${leader.team_count} team${leader.team_count === 1 ? '' : 's'}`);

export const LeaderList = ({ title, icon: Icon, leaders, empty }) => (
  <Card className="p-5">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Icon size={16} aria-hidden="true" />{title}<span className="text-xs font-normal text-slate-500">{leaders.length}</span></h3>
    {leaders.length ? <ul className="mt-3 divide-y divide-slate-100">{leaders.map((leader) => (
      <li key={leader.id} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-800">{leader.name}</span><span className="text-xs text-slate-500">{scopeLabel(leader)}</span></li>
    ))}</ul> : <p className="mt-3 text-sm text-slate-500">{empty}</p>}
  </Card>
);

export const useOrganisationOverview = () => {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/organisation/overview');
      setOverview(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load the organisation overview');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { overview, error, reload: load };
};

const Row = ({ children, value, onClick, warn }) => (
  <li className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-0" style={{ borderColor: 'var(--qflow-border)' }}>
    <span>{children}</span>
    {onClick ? <button type="button" onClick={onClick} className="qflow-accent-bg rounded-md px-2.5 py-1 text-xs font-semibold">{value}</button> : <span className={warn ? 'font-semibold' : 'qflow-accent-text font-semibold'} style={warn ? { color: '#e0a052' } : undefined}>{value}</span>}
  </li>
);

// Admin overview: what needs the Tenant Admin's decision, then a health check of people, security and leaders.
const OrganisationOverview = ({ onOpenSection, canEditAbilities = true }) => {
  const navigate = useNavigate();
  const { overview, error } = useOrganisationOverview();
  if (error) return <Card className="p-6 text-sm text-rose-700">{error}</Card>;
  if (!overview) return <Card className="p-6 text-sm text-slate-500">Loading…</Card>;
  const { organization, people, abilities, leaders, domains, pending_join_requests: pending, security = {} } = overview;
  const verified = domains.filter((domain) => domain.status === 'verified');
  const waitingDomains = domains.filter((domain) => domain.status !== 'verified');
  const decisions = [
    pending > 0 && { key: 'join', text: `${pending} ${pending === 1 ? 'person has' : 'people have'} asked to join`, action: 'Review', onClick: () => onOpenSection('members') },
    people.awaiting_approval > 0 && { key: 'approve', text: `${people.awaiting_approval} new sign-up${people.awaiting_approval === 1 ? '' : 's'} waiting for approval`, action: 'Review', onClick: () => navigate('/team-users') },
    ...waitingDomains.map((domain) => ({ key: domain.domain, text: `${domain.domain} is waiting for its DNS record`, action: 'Check', onClick: () => onOpenSection('members') })),
  ].filter(Boolean);
  return (
    <div className="space-y-5" data-testid="organisation-overview">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="qflow-home-eyebrow">Admin · {organization.display_name || organization.name}</p>
          <h2 className="text-xl font-extrabold">Run your organisation</h2>
        </div>
        <span className="qflow-home-pill qflow-home-pill-good">{organization.status || 'active'}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="People" value={people.total} hint={`${people.members} members · ${people.tenant_admins} admin${people.tenant_admins === 1 ? '' : 's'}`} />
        <Stat icon={Star} label="Resolved, last 30 days" value={overview.resolution_rate === null || overview.resolution_rate === undefined ? '–' : `${overview.resolution_rate}%`} />
        <Stat icon={Mail} label="To approve" value={pending + people.awaiting_approval} hint={`${pending} join requests · ${people.awaiting_approval} sign-ups`} />
        <Stat icon={Globe2} label="Verified domains" value={verified.length} hint={verified.map((domain) => domain.domain).join(', ') || 'None yet'} />
      </div>
      <Card className="p-5" data-testid="overview-decisions">
        <div className="flex items-center justify-between"><h3 className="text-base font-semibold">Needs your decision</h3><span className="text-xs text-slate-500">{decisions.length} item{decisions.length === 1 ? '' : 's'}</span></div>
        {decisions.length ? <ul className="mt-2">{decisions.map((item) => <Row key={item.key} value={item.action} onClick={item.onClick}>{item.text}</Row>)}</ul> : <p className="mt-2 text-sm text-slate-500">Nothing waiting for you.</p>}
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="text-base font-semibold">People and access</h3>
          <ul className="mt-2">
            {CAPABILITIES.map((ability) => <Row key={ability.key} value={`${abilities[ability.key] || 0} ${abilities[ability.key] === 1 ? 'person' : 'people'}`}>{ability.label}</Row>)}
            <Row value={`${leaders.floor.length} · ${leaders.source.length}`}>Floor Leaders · Source Leaders</Row>
          </ul>
          {canEditAbilities && <button type="button" onClick={() => onOpenSection('abilities')} className="qflow-accent-text mt-2 text-xs font-semibold hover:underline">Change in Abilities →</button>}
        </Card>
        <Card className="p-5">
          <h3 className="text-base font-semibold">Security</h3>
          <ul className="mt-2">
            <Row value={security.two_step_required ? `Required � ${security.two_step_on || 0} of ${people.total} set up` : 'Off for the organisation'} warn={security.two_step_required && (security.two_step_on || 0) < people.total}>Two-step sign-in</Row>
            <Row value={security.failed_signins_7d || 0} warn={(security.failed_signins_7d || 0) > 0}>Failed sign-ins, last 7 days</Row>
            <Row value={verified.length ? 'Verified domains available' : 'No verified domain yet'}>Invitations</Row>
          </ul>
          <button type="button" onClick={() => onOpenSection('user-login')} className="qflow-accent-text mt-2 text-xs font-semibold hover:underline">Open Sign-in history →</button>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <LeaderList title="Floor Leaders" icon={Building2} leaders={leaders.floor} empty="No Floor Leaders yet." />
        <LeaderList title="Source Leaders" icon={Star} leaders={leaders.source} empty="No Source Leaders yet." />
      </div>
    </div>
  );
};

export default OrganisationOverview;
